/**
 * The side sheet (#1717; the v6 prototype's `sideUI`): Contact, then
 * Conversation & history.
 *
 * Contact reads what the rail's Customer & Site card read (the job's on-site
 * contact, then the customer's ranked contact methods — same query key, so one
 * request). The conversation is the Activity card's feed (same query key)
 * told as day-grouped sentences: notes and close-out questions tinted, system
 * noise folded to one gray line per burst, a dot on anything new since this
 * person last opened the job, and the Activity card's own composers.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { isUmbrellaContactName } from "@/lib/vocabulary";
import type { FinishLine } from "@/lib/finish-line";
import { useJobLastViewed } from "@/lib/job-last-viewed";
import { groupTimeline, type TimelineItemLike } from "../../jobDetailHelpers";
import {
    ActivityComposer,
    FILTER_CHIPS,
    noteMeta,
    readStoredFilter,
    storeFilter,
    type Composer,
    type Filter,
} from "../activity-card";
import { JobNoteActions } from "../notes-panel";
import { isPhoneKind, rankMethods, type ContactWithMethods } from "../reference-rail";
import type { JobDetail } from "../types";
import { clockTime, groupFeedByDay, isNewSince, type FeedLine } from "./calmModel";
import { FOCUS, Quiet, QuietLink } from "./calm-ui";

const digits = (s: string) => s.replace(/[^\d+]/g, "");

// ── Contact ─────────────────────────────────────────────────────────────────

export function ContactBlock({ job, allContactsNonce }: { job: JobDetail; allContactsNonce?: number }) {
    // Job page v2 (slice 5): every contact for this customer, opened in place.
    const [showAll, setShowAll] = useState(false);
    useEffect(() => {
        if (allContactsNonce) setShowAll(true);
    }, [allContactsNonce]);
    const contactsQ = useQuery({
        queryKey: ["customer-contact-methods", job.customer_id, job.site_id],
        queryFn: () =>
            api.get<{ contacts: ContactWithMethods[] }>(
                `/api/customers/${job.customer_id}/contact-methods${job.site_id ? `?siteId=${job.site_id}` : ""}`,
            ),
        enabled: !!job.customer_id,
    });
    const ranked = rankMethods(contactsQ.data?.contacts ?? [], job.site_id);
    const topPhone = ranked.find((m) => isPhoneKind(m.kind));
    const topEmail = ranked.find((m) => /email/i.test(m.kind));
    const accountName = [topPhone, topEmail].find(
        (m) => m && m.contactName && !isUmbrellaContactName(m.contactName),
    )?.contactName;

    const onSiteName =
        job.site_contact_name && !isUmbrellaContactName(job.site_contact_name) ? job.site_contact_name : null;
    const onSitePhone = job.site_contact_phone || (onSiteName ? job.site_phone : null) || null;
    const callNumber = onSitePhone || topPhone?.value || job.site_phone || null;

    return (
        <div className="grid gap-1 border-b px-[22px] pb-4 pt-5" id="calm-contact" data-testid="calm-contact">
            <span className="text-[13px] text-muted-foreground">Contact</span>
            {onSiteName ? (
                <>
                    <span className="text-[16px] font-bold text-foreground">{onSiteName}</span>
                    <span className="text-[14px] text-foreground/80">
                        On site
                        {onSitePhone && (
                            <>
                                {" "}
                                · <span className="font-mono text-[13.5px]">{onSitePhone}</span>
                            </>
                        )}
                    </span>
                </>
            ) : (
                <span className="text-[13.5px] text-amber-700 dark:text-amber-400" data-testid="calm-contact-none">
                    No on-site contact set
                    {job.site_id && (
                        <>
                            {" · "}
                            <Link
                                to={`/sites/${job.site_id}`}
                                className={cn("font-semibold text-primary hover:underline", FOCUS)}
                            >
                                Set one
                            </Link>
                        </>
                    )}
                    {job.site_phone && (
                        <span className="block text-foreground/80">
                            Site line <span className="font-mono">{job.site_phone}</span>
                        </span>
                    )}
                </span>
            )}
            {(topPhone || topEmail) && (
                <span className="mt-1 text-[14px] text-foreground/80" data-testid="calm-contact-account">
                    {accountName && <b className="font-semibold text-foreground">{accountName}</b>}
                    {accountName && " · "}
                    {topPhone && <span className="font-mono text-[13.5px]">{topPhone.value}</span>}
                    {topPhone && topEmail && " · "}
                    {topEmail && <span className="break-all">{topEmail.value}</span>}
                    {(topPhone?.accountLevel || topEmail?.accountLevel) && (
                        <span className="block text-[13px] text-muted-foreground">
                            Account contact, may not be on site
                        </span>
                    )}
                </span>
            )}
            {!contactsQ.isLoading && ranked.length === 0 && (
                <span className="text-[13.5px] text-muted-foreground">No account contact on file</span>
            )}
            {(job.site_name || job.site_city) && (
                <span className="mt-1 text-[13.5px] text-muted-foreground" data-testid="calm-contact-site">
                    {[job.site_name, job.site_city].filter(Boolean).join(" · ")}
                </span>
            )}
            {showAll && (
                <ul className="m-0 mt-1.5 grid list-none gap-1.5 border-t p-0 pt-2" data-testid="calm-all-contacts">
                    {(contactsQ.data?.contacts ?? []).map((c) => {
                        const n = [c.firstName, c.lastName].filter(Boolean).join(" ").trim() || "No name";
                        return (
                            <li key={c.id} className="grid text-[14px] text-foreground/80">
                                <span>
                                    <b className="font-semibold text-foreground">{n}</b>
                                    {c.roleTitle && ` · ${c.roleTitle}`}
                                    {job.site_id && c.siteId === job.site_id && (
                                        <span className="text-muted-foreground"> · this location</span>
                                    )}
                                </span>
                                {c.methods.map((m) => (
                                    <span key={m.id} className="break-all font-mono text-[13px]">
                                        {m.value}
                                        {m.label && (
                                            <span className="font-sans text-muted-foreground"> · {m.label}</span>
                                        )}
                                    </span>
                                ))}
                            </li>
                        );
                    })}
                </ul>
            )}
            <span className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
                {callNumber && <QuietLink href={`tel:${digits(callNumber)}`}>Call</QuietLink>}
                {callNumber && <QuietLink href={`sms:${digits(callNumber)}`}>Text</QuietLink>}
                {topEmail && <QuietLink href={`mailto:${topEmail.value}`}>Email</QuietLink>}
                {(contactsQ.data?.contacts.length ?? 0) > 1 && (
                    <Quiet
                        tone="mute"
                        onClick={() => setShowAll((o) => !o)}
                        aria-expanded={showAll}
                        data-testid="calm-all-contacts-toggle"
                    >
                        All contacts · {contactsQ.data!.contacts.length} {showAll ? "▴" : "▸"}
                    </Quiet>
                )}
                {job.customer_id && (
                    <QuietLink to={`/customers/${job.customer_id}`} tone="mute">
                        Manage contacts
                    </QuietLink>
                )}
            </span>
        </div>
    );
}

// ── Conversation & history ──────────────────────────────────────────────────

const FEED_CAP = 14;

export function Conversation({
    job,
    finishLine,
    now,
    composeRequest,
}: {
    job: JobDetail;
    finishLine: FinishLine | null;
    /** For tests and proofs. */
    now?: Date;
    /** A job action asked to open a composer (job page v2). */
    composeRequest?: { kind: "note" | "message" | "call"; n: number };
}) {
    const qc = useQueryClient();
    const { user } = useAuth();
    const [composer, setComposer] = useState<Composer>(null);
    const rootRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        if (!composeRequest) return;
        setComposer(composeRequest.kind);
        rootRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [composeRequest?.n]);
    const [filter, setFilter] = useState<Filter>(readStoredFilter);
    const [showAll, setShowAll] = useState(false);
    const [openNoise, setOpenNoise] = useState<Set<string>>(new Set());
    const { lastViewed } = useJobLastViewed(job.id);
    const activityQ = useQuery({
        queryKey: ["activity-timeline", "job", job.id, 50],
        queryFn: () =>
            api.get<{ timeline: TimelineItemLike[] }>(
                `/api/activity?entityType=job&entityId=${encodeURIComponent(job.id)}&limit=50`,
            ),
    });
    const refresh = () => {
        qc.invalidateQueries({ queryKey: ["activity-timeline"] });
        qc.invalidateQueries({ queryKey: ["job", job.id] });
    };

    const allEntries = groupTimeline(activityQ.data?.timeline ?? []);
    // Job page v2 (slice 5): pinned notes float to the top, as on the classic
    // page, with what pinning means ("techs and PDFs see this").
    const pinnedNotes = allEntries.filter((e) => noteMeta(e)?.pinned);
    const entries = allEntries.filter((e) => !noteMeta(e)?.pinned);
    const filtered = filter === "all" ? entries : entries.filter((e) => e.category === filter);
    const capped = showAll ? filtered : filtered.slice(0, FEED_CAP);
    const days = groupFeedByDay(capped, now);

    const jobOpen = !["completed", "closed", "cancelled"].includes(job.status);
    const techNote = jobOpen ? job.tech_instructions : null;
    const question = finishLine?.promises.find((p) => p.id === "questions" && p.state === "open") ?? null;

    const toggleNoise = (id: string) =>
        setOpenNoise((s) => {
            const n = new Set(s);
            if (n.has(id)) n.delete(id);
            else n.add(id);
            return n;
        });

    const line = (l: FeedLine) => {
        if (l.kind === "noise") {
            const open = openNoise.has(l.id);
            const n = l.entries.length;
            return (
                <div key={l.id}>
                    <button
                        type="button"
                        className={cn(
                            "grid w-full grid-cols-[64px_minmax(0,1fr)] gap-2 py-1 text-left text-[13px] text-muted-foreground hover:text-foreground",
                            FOCUS,
                        )}
                        aria-expanded={open}
                        onClick={() => toggleNoise(l.id)}
                        data-testid="calm-feed-noise"
                    >
                        <span>{clockTime(l.entries[l.entries.length - 1]!.timestamp)}</span>
                        <span>
                            {open ? "▾" : "▸"} {n === 1 ? l.entries[0]!.title : `${n} system updates`}
                        </span>
                    </button>
                    {open &&
                        l.entries.map((e) => (
                            <div
                                key={e.id}
                                className="grid grid-cols-[64px_minmax(0,1fr)] gap-2 py-0.5 text-[13px] text-muted-foreground"
                            >
                                <span>{clockTime(e.timestamp)}</span>
                                <span>
                                    {e.actor ? `${e.actor}: ` : ""}
                                    {e.title}
                                    {e.detail ? ` · ${e.detail}` : ""}
                                </span>
                            </div>
                        ))}
                </div>
            );
        }
        const e = l.entry;
        const note = noteMeta(e);
        const isNew = isNewSince(e.timestamp, lastViewed);
        const tinted = !!note;
        return (
            <div
                key={e.id}
                className="grid grid-cols-[64px_minmax(0,1fr)] gap-2 py-1 text-[14px] text-foreground/80"
                data-testid="calm-feed-entry"
                data-new={isNew || undefined}
            >
                <span className="pt-px text-[12.5px] text-muted-foreground">{clockTime(e.timestamp)}</span>
                <span
                    className={cn(
                        "min-w-0",
                        tinted && "rounded-lg bg-amber-50 px-2.5 py-2 text-foreground dark:bg-amber-500/10",
                    )}
                >
                    {isNew && (
                        <span
                            className="mr-1.5 inline-block h-[7px] w-[7px] rounded-full bg-amber-600 align-[1px]"
                            title="New since you last opened this job"
                            aria-label="New"
                            role="img"
                        />
                    )}
                    {e.actor && <b className="font-semibold text-foreground">{e.actor}: </b>}
                    {note ? (note.pinned ? "Pinned note · techs and PDFs see this" : "Note") : e.title}
                    {e.items.length > 1 && <span className="text-muted-foreground"> · {e.items.length} events</span>}
                    {e.detail && (
                        <span
                            className={cn(
                                "block whitespace-pre-wrap",
                                note ? "mt-0.5 text-foreground" : "text-[13.5px] text-foreground/70",
                            )}
                        >
                            {e.detail}
                        </span>
                    )}
                    {note && (
                        <JobNoteActions
                            jobId={job.id}
                            noteId={note.noteId}
                            body={e.detail ?? ""}
                            pinned={note.pinned}
                            authorUserId={note.authorUserId}
                            createdAt={note.createdAt}
                            currentUserId={user?.id ?? null}
                            onChanged={refresh}
                        />
                    )}
                </span>
            </div>
        );
    };

    return (
        <div ref={rootRef} className="px-[22px] pb-5 pt-4" id="calm-conversation" data-testid="calm-conversation">
            <div className="mb-1.5 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                <h2 className="m-0 text-[16px] font-bold text-foreground">Conversation &amp; history</h2>
                <span className="flex gap-x-3.5">
                    {(
                        [
                            ["note", "+ Note"],
                            ["message", "Message"],
                            ["call", "Log call"],
                        ] as const
                    ).map(([k, label]) => (
                        <Quiet
                            key={k}
                            tone={composer === k ? "link" : "mute"}
                            aria-expanded={composer === k}
                            onClick={() => setComposer(composer === k ? null : k)}
                            data-testid={`calm-compose-${k}`}
                        >
                            {label}
                        </Quiet>
                    ))}
                </span>
            </div>
            <div className="space-y-3">
                <ActivityComposer job={job} composer={composer} onDone={() => setComposer(null)} />
            </div>
            {techNote && (
                <div
                    className="mt-2 rounded-lg bg-amber-50 px-2.5 py-2 text-[14px] dark:bg-amber-500/10"
                    data-testid="calm-tech-note"
                >
                    <b className="font-semibold text-amber-800 dark:text-amber-300">
                        Pinned · tech instructions{job.assigned_to_name ? ` for ${job.assigned_to_name}` : ""}
                    </b>
                    <span className="block whitespace-pre-wrap text-foreground">{techNote}</span>
                </div>
            )}
            {pinnedNotes.length > 0 && (
                <div className="mt-2" data-testid="calm-pinned-notes">
                    {pinnedNotes.map((e) => line({ kind: "entry", entry: e }))}
                </div>
            )}
            {question && (
                <div
                    className="mt-2 rounded-lg bg-amber-50 px-2.5 py-2 text-[14px] dark:bg-amber-500/10"
                    data-testid="calm-question"
                >
                    <b className="font-semibold text-foreground">Open question · </b>
                    {question.title.replace(/ answered$/, "")}
                    <span className="block text-foreground/80">{question.detail}</span>
                    <QuietLink to={`/close-out?job=${job.id}`}>Open in Closeout</QuietLink>
                </div>
            )}
            <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1" role="group" aria-label="Show">
                {FILTER_CHIPS.map((c) => (
                    <button
                        key={c.key}
                        type="button"
                        aria-pressed={filter === c.key}
                        className={cn(
                            "bg-transparent p-0 text-[13px]",
                            FOCUS,
                            filter === c.key
                                ? "font-bold text-foreground"
                                : "text-muted-foreground hover:text-foreground hover:underline",
                        )}
                        onClick={() => {
                            setFilter(c.key);
                            storeFilter(c.key);
                            setShowAll(false);
                        }}
                    >
                        {c.label}
                    </button>
                ))}
            </div>
            {activityQ.isLoading ? (
                <p className="m-0 py-3 text-[14px] text-muted-foreground">Loading the history…</p>
            ) : filtered.length === 0 ? (
                <p className="m-0 py-3 text-[14px] text-muted-foreground">Nothing here yet.</p>
            ) : (
                days.map((d) => (
                    <div key={d.day}>
                        <div className="mb-1 mt-3.5 text-[12.5px] font-bold uppercase tracking-wide text-muted-foreground">
                            {d.day}
                        </div>
                        {d.lines.map(line)}
                    </div>
                ))
            )}
            {!showAll && filtered.length > FEED_CAP && (
                <Quiet className="mt-2" onClick={() => setShowAll(true)} data-testid="calm-feed-show-all">
                    Show all {filtered.length}
                </Quiet>
            )}
        </div>
    );
}
